// Contact-masking helpers — defraud-sacred per [[feedback-contact-masking]].
//
// Customer phone numbers and email addresses must NEVER appear unmasked
// in the retailer-facing UI. These helpers are the single source of
// truth for the visual mask format. Defence-in-depth: even if a backend
// slip-up returns unmasked data, every render passes through these
// helpers so the UI still masks.
//
// WHY: keeps communication on-platform, protects the 3.3% commission
// model, prevents retailers bypassing TKOS for direct payment, aligns
// with the broader defraud system philosophy.
//
// SCOPE: customer contacts only. Trade contacts (the retailer's own
// trade contacts in the `trade_contacts` table — Dave the Plumber etc.)
// and supplier contacts are platform-to-platform and are NOT masked —
// pass them through directly without using these helpers.
//
// ADMIN PORTAL EXCEPTION: Steve's admin portal sees unmasked values.
// That bypass is implemented server-side via an `x-admin-portal: 1`
// header; these client helpers are never wired into the admin portal.

const NBSP = " ";

/**
 * Mask a customer phone number for retailer-facing UI.
 *
 * Format: `+44 7822 ••• •••`
 *   - keep country code + first group of 4 digits
 *   - mask the remaining digits with bullets, preserving grouping
 *
 * Handles common UK shapes (+44…, 0…, 07…) and falls back to a
 * fully-masked placeholder for malformed input so no structure leaks.
 *
 * Returns "—" for absent values (null/undefined/empty string) so empty
 * fields don't render as a row of bullets.
 */
export function maskPhone(raw: string | null | undefined): string {
  if (raw == null) return "—";
  const trimmed = String(raw).trim();
  if (!trimmed) return "—";

  // Idempotent guard — if the backend already masked the value
  // (defence-in-depth: server masks customer contact data on response),
  // don't re-mask and degrade it.
  if (trimmed.indexOf("•") !== -1) return trimmed;

  // Strip non-digit chars except the leading +.
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/[^\d]/g, "");

  // Reject obviously malformed numbers — show generic placeholder.
  if (digits.length < 7) {
    return `•••${NBSP}•••${NBSP}••••`;
  }

  // Normalise UK 07xxx → 447xxx.
  let normalised = digits;
  if (!hasPlus && digits.startsWith("0")) {
    normalised = "44" + digits.slice(1);
  } else if (!hasPlus && digits.startsWith("44")) {
    normalised = digits;
  } else if (hasPlus) {
    normalised = digits;
  }

  // Show "+CC GROUP1 ••• •••" pattern — the first 4 digits of the
  // mobile/landline are visible so the retailer can sanity-check the
  // record matches the customer they're looking at.
  if (normalised.length >= 11) {
    const cc = normalised.slice(0, 2);             // e.g. "44"
    const group1 = normalised.slice(2, 6);         // e.g. "7822"
    return `+${cc}${NBSP}${group1}${NBSP}•••${NBSP}•••`;
  }

  // Short numbers — show country code only.
  if (normalised.length >= 5) {
    const cc = normalised.slice(0, 2);
    return `+${cc}${NBSP}•••${NBSP}•••${NBSP}••••`;
  }

  return `•••${NBSP}•••${NBSP}••••`;
}

/**
 * Mask a customer email for retailer-facing UI.
 *
 * Format: `e••••@h•••••.co.uk`
 *   - first letter of local part visible
 *   - first letter of domain visible
 *   - TLD visible (last `.`-separated segment, plus `.co.uk` style
 *     two-part TLDs)
 *
 * Returns "—" for absent values (null/undefined/empty string).
 * Malformed addresses (missing @ or .) fall back to masking everything
 * except the first character of the local part so no structure leaks.
 */
export function maskEmail(raw: string | null | undefined): string {
  if (raw == null) return "—";
  const trimmed = String(raw).trim();
  if (!trimmed) return "—";

  // Idempotent guard — already-masked input passes through unchanged.
  if (trimmed.indexOf("•") !== -1) return trimmed;

  const atIdx = trimmed.indexOf("@");
  if (atIdx <= 0) {
    // Malformed — mask everything except first char.
    return trimmed.charAt(0) + "••••••";
  }
  const local = trimmed.slice(0, atIdx);
  const domain = trimmed.slice(atIdx + 1);

  // Split domain into label + tld(s).
  const dotIdx = domain.indexOf(".");
  if (dotIdx <= 0) {
    return trimmed.charAt(0) + "••••••";
  }
  const domainLabel = domain.slice(0, dotIdx);
  const tld = domain.slice(dotIdx); // includes the leading dot

  const localMasked = (local.charAt(0) || "•") + "••••";
  const domainMasked = (domainLabel.charAt(0) || "•") + "•••••";
  return `${localMasked}@${domainMasked}${tld}`;
}

/**
 * Mask a customer-facing contact field generically. Detects email
 * (contains @) vs phone and dispatches.
 */
export function maskContact(raw: string | null | undefined): string {
  if (raw == null) return "—";
  const s = String(raw).trim();
  if (!s) return "—";
  return s.includes("@") ? maskEmail(s) : maskPhone(s);
}
